import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format, addWeeks, addMonths, getDay, setDay } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Building2, StickyNote } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Room, Booking, BookingStatus } from '@/types/booking';
import { Textarea } from '@/components/ui/textarea';
import { ToggleWithLabel } from '@/components/ui/toggle-with-label';
import { TimeSelect } from '@/components/ui/time-select';
import { useStaff } from '@/contexts/StaffContext';
import { staffConfig } from '@/config/staff';
import { useBookings } from '@/contexts/BookingContext';
import { validateBookingTime } from '@/lib/booking-utils';
import { useToast } from '@/hooks/use-toast';
import { ConflictWarningDialog } from './ConflictWarningDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDevice } from '@/hooks/use-device';

interface RecurringEventsProps {
  rooms: Room[];
  onBookingsCreate: (bookings: Booking[]) => boolean;
  onClose: () => void;
  onComplete?: () => void;
}

type RecurrenceType = 'weekly' | 'biweekly' | 'monthly';

const statusStyles = {
  paid: 'px-3 py-1.5 w-[200px] bg-blue-100 border-l-4 border-blue-500 text-blue-800 hover:bg-blue-200/80 transition-all duration-200',
  booked: 'px-3 py-1.5 w-[200px] bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800 hover:bg-emerald-200/80 transition-all duration-200',
  pending: 'px-3 py-1.5 w-[200px] bg-amber-100 border-l-4 border-amber-500 text-amber-800 hover:bg-amber-200/80 transition-all duration-200'
};

export function RecurringEvents({ 
  rooms, 
  onBookingsCreate, 
  onClose,
  onComplete 
}: RecurringEventsProps) {
  const { isMobile } = useDevice();
  const { selectedStaff } = useStaff();
  const { checkDuplicate } = useBookings();
  const { toast } = useToast();
  const [open, setOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictBooking, setConflictBooking] = useState<Booking | null>(null);
  const [pendingBookings, setPendingBookings] = useState<Booking[]>([]);

  const [form, setForm] = useState({
    isHavenBooking: false,
    roomId: rooms[0]?.id || 0,
    clientName: '',
    bookedBy: selectedStaff?.name || '',
    date: new Date(),
    timeSlot: '09:00',
    duration: '1',
    status: 'pending' as BookingStatus,
    recurrenceType: 'weekly' as RecurrenceType,
    occurrences: 1,
    amount: selectedStaff ? staffConfig.find(s => s.name === selectedStaff.name)?.defaults.amount || 0 : 0,
    isFullDay: false,
    hasNotes: false,
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!form.bookedBy) {
      newErrors.bookedBy = 'Staff member is required';
    }

    if (form.bookedBy !== 'SuHe' && !form.clientName?.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (form.occurrences < 1 || form.occurrences > 52) {
      newErrors.occurrences = 'Number of occurrences must be between 1 and 52';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateBookings = () => {
    const bookings: Booking[] = [];
    let currentDate = form.date;
    const dayOfWeek = getDay(currentDate);

    for (let i = 0; i < form.occurrences; i++) {
      const roomsToBook = form.isHavenBooking ? rooms : [rooms.find(r => r.id === form.roomId)!];
      
      roomsToBook.forEach(room => {
        bookings.push({
          id: Date.now() + i + room.id,
          roomId: room.id,
          date: format(currentDate, 'yyyy-MM-dd'),
          timeSlot: form.isFullDay ? '09:00' : form.timeSlot,
          duration: form.isFullDay ? 14 : parseInt(form.duration),
          bookedBy: form.bookedBy,
          clientName: form.bookedBy === 'SuHe' ? 'SuHe' : form.clientName,
          status: form.status,
          revenue: {
            amount: form.bookedBy === 'SuHe' ? 0 : form.amount,
            hasNotes: form.hasNotes,
            notes: form.hasNotes ? form.notes : undefined
          }
        });
      });

      switch (form.recurrenceType) {
        case 'weekly':
          currentDate = addWeeks(currentDate, 1);
          break;
        case 'biweekly':
          currentDate = addWeeks(currentDate, 2);
          break;
        case 'monthly':
          currentDate = setDay(addMonths(currentDate, 1), dayOfWeek);
          break;
      }
    }

    return bookings;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const bookings = generateBookings();
      
      // Check each booking for time validation and conflicts
      for (const booking of bookings) {
        // Validate booking time
        const timeError = validateBookingTime(booking);
        if (timeError) {
          toast({
            title: "Invalid Time",
            description: timeError,
            variant: "destructive"
          });
          return;
        }

        // Check for conflicts
        const conflict = checkDuplicate(booking);
        if (conflict) {
          setConflictBooking(booking);
          setPendingBookings(bookings);
          setShowConflictWarning(true);
          return;
        }
      }

      // If no conflicts, create all bookings
      const success = onBookingsCreate(bookings);

      if (success) {
        toast({
          title: "Success",
          description: `Created ${bookings.length} recurring bookings`,
        });
        // Call onComplete to switch to monthly view
        onComplete?.();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAlternativeSelect = (newDate: string, newTime: string) => {
    if (conflictBooking && pendingBookings.length > 0) {
      // Adjust all bookings based on the new date/time
      const firstBooking = pendingBookings[0];
      const daysDiff = (new Date(newDate).getTime() - new Date(firstBooking.date).getTime()) / (1000 * 60 * 60 * 24);
      
      const adjustedBookings = pendingBookings.map(booking => ({
        ...booking,
        date: format(new Date(new Date(booking.date).getTime() + daysDiff * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        timeSlot: newTime
      }));

      const success = onBookingsCreate(adjustedBookings);

      if (success) {
        toast({
          title: "Success",
          description: `Created ${adjustedBookings.length} recurring bookings`,
        });
        onComplete?.();
      }
    }
    onClose();
  };

  const handleDurationChange = (value: string) => {
    if (value === 'full') {
      setForm(prev => ({ ...prev, duration: '14', isFullDay: true }));
    } else {
      setForm(prev => ({ ...prev, duration: value, isFullDay: false }));
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setForm(prev => ({ ...prev, date }));
      setCalendarOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleStaffSelect = (value: string) => {
    const staffDefaults = staffConfig.find(s => s.name === value)?.defaults;
    setForm(prev => ({
      ...prev,
      bookedBy: value,
      clientName: value === 'SuHe' ? 'SuHe' : '',
      amount: 0, // Always default to 0€
      status: staffDefaults?.status || 'pending'
    }));
  };

  const isSuHeSelected = form.bookedBy === 'SuHe';

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className={cn(
          "max-w-2xl",
          isMobile && "w-[95vw] p-4 max-h-[95vh]"
        )}>
          <DialogHeader>
            <DialogTitle>Create Recurring Booking</DialogTitle>
          </DialogHeader>

          <ScrollArea className={cn(
            "pr-4 -mr-4",
            isMobile ? "max-h-[calc(95vh-8rem)]" : "max-h-[calc(85vh-8rem)]"
          )}>
            <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <ToggleWithLabel
                  id="haven-booking"
                  checked={form.isHavenBooking}
                  onChange={(checked) => 
                    setForm(prev => ({
                      ...prev,
                      isHavenBooking: checked
                    }))
                  }
                  label="Haven Booking"
                  leftIcon={<Building2 className="h-4 w-4" />}
                />
              </div>

              {form.isHavenBooking ? (
                <div className={cn(
                  "bg-purple-50 rounded-lg border border-purple-100",
                  isMobile ? "p-3" : "p-4"
                )}>
                  <div className="flex items-center gap-2 text-purple-800">
                    <Building2 className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Haven Booking Mode</p>
                      <p className="text-sm text-purple-700">
                        This booking will reserve all {rooms.length} rooms simultaneously
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="room">Room</Label>
                  <Select
                    value={form.roomId.toString()}
                    onValueChange={(value) => setForm(prev => ({ ...prev, roomId: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map(room => (
                        <SelectItem key={room.id} value={room.id.toString()}>
                          {room.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="bookedBy">Booked By</Label>
                <Select 
                  value={form.bookedBy}
                  onValueChange={handleStaffSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffConfig.map(staff => (
                      <SelectItem key={staff.id} value={staff.name}>
                        <span className={cn("px-2 py-1 rounded-md", staff.color)}>
                          {staff.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.bookedBy && <p className="text-sm text-red-500">{errors.bookedBy}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientName">Client Company</Label>
                <Input
                  id="clientName"
                  value={isSuHeSelected ? 'SuHe' : form.clientName}
                  onChange={(e) => setForm(prev => ({ ...prev, clientName: e.target.value }))}
                  disabled={isSuHeSelected}
                  className={errors.clientName ? 'border-red-500' : ''}
                />
                {errors.clientName && <p className="text-sm text-red-500">{errors.clientName}</p>}
              </div>

              <div className="space-y-2">
                <Label>Start Date & Time</Label>
                <div className={cn(
                  "flex gap-2",
                  isMobile && "flex-col"
                )}>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          isMobile ? "w-full" : "w-[280px]",
                          !form.date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.date ? format(form.date, 'PPP') : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.date}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <TimeSelect 
                    value={form.timeSlot}
                    onChange={(value) => setForm(prev => ({ ...prev, timeSlot: value }))}
                    className={isMobile ? "w-full" : undefined}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Select 
                  value={form.isFullDay ? 'full' : form.duration}
                  onValueChange={handleDurationChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full" className="font-medium text-blue-600">
                      Full Day (9:00-23:00)
                    </SelectItem>
                    <SelectItem value="1">1 hour</SelectItem>
                    {Array.from({ length: 7 }, (_, i) => (
                      <SelectItem key={i + 2} value={(i + 2).toString()}>
                        {i + 2} hours
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Recurrence Type</Label>
                <Select
                  value={form.recurrenceType}
                  onValueChange={(value: RecurrenceType) => 
                    setForm(prev => ({ ...prev, recurrenceType: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select recurrence type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every Two Weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="occurrences">Number of Occurrences</Label>
                <div className={cn(
                  "flex gap-4",
                  isMobile && "flex-col"
                )}>
                  <Input
                    id="occurrences"
                    type="number"
                    min="1"
                    max="52"
                    value={form.occurrences}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      occurrences: parseInt(e.target.value) || 1
                    }))}
                    className={cn(
                      isMobile ? "w-full" : "w-24",
                      errors.occurrences && 'border-red-500'
                    )}
                  />
                  <Select
                    value={form.occurrences.toString()}
                    onValueChange={(value) => setForm(prev => ({
                      ...prev,
                      occurrences: parseInt(value)
                    }))}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select occurrences" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} {num === 1 ? 'occurrence' : 'occurrences'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors.occurrences && (
                  <p className="text-sm text-red-500">{errors.occurrences}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={form.status}
                  onValueChange={(value) => setForm({...form, status: value as BookingStatus})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">
                      <div className={cn("px-3 py-1 rounded-md", statusStyles.paid)}>
                        Paid
                      </div>
                    </SelectItem>
                    <SelectItem value="booked">
                      <div className={cn("px-3 py-1 rounded-md", statusStyles.booked)}>
                        Booked
                      </div>
                    </SelectItem>
                    <SelectItem value="pending">
                      <div className={cn("px-3 py-1 rounded-md", statusStyles.pending)}>
                        Pending
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount (€)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    €
                  </span>
                  <Input
                    id="amount"
                    type="text"
                    inputMode="decimal"
                    pattern="^\d*\.?\d{0,2}$"
                    className="pl-7"
                    value={isSuHeSelected ? '0' : form.amount.toString()}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = value.split('.');
                      if (parts.length > 2) return;
                      if (parts[1]?.length > 2) return;
                      
                      setForm(prev => ({
                        ...prev,
                        amount: value === '' ? 0 : parseFloat(value) || 0
                      }));
                    }}
                    disabled={isSuHeSelected}
                    style={{ appearance: 'textfield' }}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-2">
                <ToggleWithLabel
                  id="notes-enabled"
                  checked={form.hasNotes}
                  onChange={(checked) => 
                    setForm(prev => ({
                      ...prev,
                      hasNotes: checked
                    }))
                  }
                  label="Add Notes"
                  leftIcon={<StickyNote className="h-4 w-4" />}
                />

                {form.hasNotes && (
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Add any notes..."
                      value={form.notes}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        notes: e.target.value
                      }))}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              {isMobile && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className={cn(
                  "bg-purple-600 hover:bg-purple-700",
                  isMobile && "flex-1"
                )}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Recurring Bookings'}
              </Button>
            </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {showConflictWarning && conflictBooking && (
        <ConflictWarningDialog
          conflict={checkDuplicate(conflictBooking)!}
          rooms={[rooms.find(r => r.id === conflictBooking.roomId)!]}
          open={showConflictWarning}
          onClose={() => setShowConflictWarning(false)}
          onSelectAlternative={handleAlternativeSelect}
        />
      )}
    </>
  );
}